import { Inject, Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { APP_ENVIRONMENT, TranslationService } from '@ace/ace';

type Dictionary = Record<string, string>;

/**
 * `@ace/ace` resolves its own dictionaries from the shared asset host
 * (`assets/ace/i18n/translation_<code>.json`). Those files currently 404 for
 * every language while the rest of that directory serves normally, so the
 * library's `translate()` falls through to returning the raw key — which is
 * why the sidebar, table headers and built-in labels stay English no matter
 * which language is selected.
 *
 * Overriding `init` points the library at the app's own `assets/i18n/<code>.json`
 * — the same dictionaries ngx-translate consumes — so both translation systems
 * are driven from one set of files instead of two.
 */
@Injectable()
export class LocalTranslationService extends TranslationService {
  private readonly reportedMissingKeys = new Set<string>();

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(APP_ENVIRONMENT) environment: any,
  ) {
    super(httpClient, environment);
    if (isDevMode() && typeof window !== 'undefined') {
      // `aceMissingKeys()` in the browser console dumps what still needs keys.
      (window as any).aceMissingKeys = () => this.missingKeys();
    }
  }

  override async init(languageCode: string): Promise<void> {
    const code = (languageCode || 'en').toLowerCase();
    // `currentLanguageCode` and `translations` are declared private on the base
    // class, but writing them is the only way to reuse its `translate()`.
    const self = this as any;
    self.currentLanguageCode = code;

    const dictionary =
      (await this.fetchDictionary(code)) ??
      (code === 'en' ? null : await this.fetchDictionary('en'));

    const translations: Map<string, string> = self.translations;
    translations.clear();
    for (const [key, value] of Object.entries(dictionary ?? {})) {
      translations.set(key, value ?? '');
    }
  }

  /**
   * Menu labels come from the backend, so the dictionary can silently fall out
   * of step with what the API returns. Surfacing misses in dev turns "some
   * items are still English" into an exact, copyable list.
   */
  override translate(key: string | null | undefined): string {
    if (key && isDevMode()) {
      const self = this as any;
      const hit = (self.translations as Map<string, string>).get(key);
      if ((!hit || !hit.trim()) && !this.reportedMissingKeys.has(key)) {
        this.reportedMissingKeys.add(key);
        console.warn(
          `[i18n] no "${self.currentLanguageCode}" translation for ${JSON.stringify(key)}`,
        );
      }
    }
    return super.translate(key);
  }

  /** Every key seen this session with no entry in the active dictionary. */
  missingKeys(): string[] {
    return [...this.reportedMissingKeys].sort();
  }

  private async fetchDictionary(code: string): Promise<Dictionary | null> {
    try {
      return await firstValueFrom(
        this.httpClient.get<Dictionary>(`./assets/i18n/${code}.json`),
      );
    } catch {
      return null;
    }
  }
}
